import { createApp } from "vue";
import router from "@/routing";
import store from "@/store";
import App from "./App.vue";

import { initApiAxios } from "./infrastructure/api-axios";

import Spinner from "@/components/Spinner";

// CSS
import "@/assets/css/style.scss";
import "@/assets/css/style.bbv.scss";

// date serialization to JSON (correct timezone)
import dateSerializer from "@/regira_modules/extensions/date-extensions";
dateSerializer.use();

import appConfig, { createConfig, useConfig } from "@/app-config";
import { getData } from "@/infrastructure/data-functions";
import { getOrderFromUrl } from "@/infrastructure/order-functions";
import guardRoute from "@/infrastructure/route-guard";

const urlParams = new URLSearchParams(window.location.search);
let appId = urlParams.get("appId"); // test-webshop as backup
const orderId = urlParams.get("orderId");
const initialOrder = getOrderFromUrl();
const isInitialOrderExpired = initialOrder != null && Date.parse(initialOrder.expires) < new Date();
const klasId = initialOrder ? initialOrder.klasId || (initialOrder.klas && initialOrder.klas.id) : urlParams.get("klas");
const discount = initialOrder ? initialOrder.discount : 0;

let isDisabled = false;
let isMaintenance = false;

fetch(`${appConfig.baseUrl}/config.json?v=${appConfig.version}`)
  .then((response) => {
    return response.json();
  })
  .then((config) => {
    const processedConfig = createConfig(config);
    if (!appId) {
      appId = processedConfig.defaultAppId;
    }

    // create axios using processed config
    initApiAxios(processedConfig);
    // make sure appId stays in the querystring
    guardRoute(router, store);
  })
  .then(async () => {
    if (appId) {
      store.commit("setAppId", appId);
    }
    const orderGuid = initialOrder != null ? initialOrder.guid : undefined;
    return await getData(store.state.appId, { klasId, orderId, orderGuid });
  })
  // process data
  .then(async (data) => {
    // disabled?
    isDisabled = data.disabled;

    // webshop settings
    store.commit("webshop/setWebshop", {
      ...data.siteSettings,
      disabled: data.disabled,
      hasPreviousPaidOrder: data.hasPreviousPaidOrder,
      discount: (data.order && data.order.discount) || (data.hasPreviousPaidOrder ? 0 : discount) || 0,
    });

    // klas
    if (data.classes) {
      store.commit("data/setKlassen", data.classes);
    }
    // categories
    if (data.categories) {
      store.commit("data/setCategories", { items: data.categories, isNewCustomer: data.order?.isNewCustomer });
    }
    // products
    if (data.products) {
      store.commit("data/setProductTree", { items: data.products, isNewCustomer: data.order?.isNewCustomer });
    }

    // order
    if (data.order) {
      await store.dispatch("orders/setOrder", data.order);
    } else {
      if (klasId) {
        store.dispatch("orders/submitKlas", { klasId });
      }
      // initial order
      if (initialOrder && !isInitialOrderExpired) {
        store.dispatch("orders/setOrder", initialOrder);
      }
    }
  })
  // handle errors & check maintenance
  .catch((ex) => {
    if (ex.response && ex.response.data && ex.response.data.message === "Maintenance") {
      isMaintenance = true;
      store.commit("data/setMaintenance", isMaintenance);
      return;
    }
    console.error("Loading failed", { ex, store });
  })
  .then(() => {
    // create app
    const app = createApp(App);
    app.use(router);
    app.use(store);

    app.component("Spinner", Spinner);
    return app;
  })
  // mount app
  .then((app) => {
    console.debug("Ready", {
      app,
      appConfig: useConfig(),
      store,
      router,
      isMaintenance,
      isDisabled,
      initialOrder,
    });
    app.mount("#app");

    if (isMaintenance) {
      router.push({ name: "maintenance", query: { u: location.pathname + location.search } });
      return;
    }

    // -> moved to route-guard
    // if (isDisabled) {
    //   router.push({ name: "disabled-webshop", query: { appId, u: location.pathname + location.search } });
    //   return;
    // }

    const order = store.state.orders.order;
    if (order.id) {
      if (["paid", "failed"].includes(order.paymentStatus)) {
        router.push({ name: "bevestiging", query: { orderId: order.id } });
      } else {
        router.push({ name: "besteloverzicht", query: { orderId: order.id } });
      }
    } else {
      if (initialOrder && isInitialOrderExpired) {
        router.push({ name: "error", query: { msg: "Je persoonlijke toegang tot de webshop is verlopen" } });
      }
    }
  });
